import { AfterViewInit, Component, HostBinding, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LMFieldBaseComponent } from '@field/lm-field-base.component';
import { LMModel, LMModelChangedEvent } from '@form/model';
import { SohoEditorComponent } from 'ids-enterprise-ng';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';

enum LMRichTextTypeDisplayEnum {
  PLAIN = 0,
  DISPLAY_AS_TEXT,
}

@Component({
  selector:    'lm-richtext',
  templateUrl: './lm-richtext.component.html',
  styleUrls:   [ 'lm-richtext.component.scss' ],
  })
export class LMRichtextComponent extends LMFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(SohoEditorComponent) editor: SohoEditorComponent;

  @HostBinding('attr.data-automation-id') get dataFieldAutomationId() {
    return `lm-${this.fieldAutomationId}-field`;
  }

  @Input() layoutObject: LMFormLayoutField;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;
  @Input() parentIsFormLayout: boolean;

  public displayType: LMRichTextTypeDisplayEnum;
  public richTextValue: string;
  public isSingleLine = false;
  public layoutDisplayType;
  public rows = {};

  constructor(protected lmFieldServiceAdapter: LMFieldServiceAdapter) {
    super(lmFieldServiceAdapter);
    this.layoutDisplayType = LMRichTextTypeDisplayEnum;
  }

  // events
  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);

    this.setFieldClasses();

    if (this.layoutObject.lines === 1) {
      this.isSingleLine = true;
    }

    // no options needed
    if (this.displayAsText) {
      this.displayType = this.layoutDisplayType.DISPLAY_AS_TEXT;

    } else {
      this.displayType = this.layoutDisplayType.PLAIN;

      // see JFieldRichTextArea.java > DEFAULT_TEXTAREA_LINES
      const lines = (this.layoutObject.lines === undefined || this.layoutObject.lines === 0) ? 8 : this.layoutObject.lines;
      this.rows = {
        editor: lines,
        source: lines
      };

      this.subscribeToInlineError();
    }

    // first time
    this.richTextValue = this.modelField.value;

    this.subscriptionArray.push(this.model.modelChanged

      .subscribe(( modelChangedEvent: LMModelChangedEvent ) => {
        // need to always set the value regardless of eventType
        this.richTextValue = this.modelField.value;

        // The editor controls are a textarea and a div, which switch
        // between the two depending on what mode the editor is in.
        // Currently, the editor angular wrapper is not written in a way
        // to properly support this, so we need to get the actual
        // jquery editor control to know what is the currently visible
        // element and set the internal values appropriately
        if (modelChangedEvent.eventType === 'set' || modelChangedEvent.eventType === 'update') {
          this.setValue();
        }

        // state could have changed
        this.setState();
      })
    );

    // validity conditions listen for evaluateConditions notification
    if (this.layoutObject.validity) {
      this.subscriptionArray.push(
        this.lmFieldServiceAdapter.lmConditionUtilService.evaluateConditions.subscribe( (status: boolean) => {
          this.setState();
        })
      );
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    // need to set state for initial load
    this.setState();
    this.setValue();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  onBlur(event) {
    this.modelField.value = this.getValue();

    // don't update this.richTextValue here; causes selection to be lost
    super.onBlur(event);
  }

  onRichTextChange() {
    // Need to update the value here so text replacement changes are
    // seen
    this.modelField.value = this.getValue();
    this.onChange();
  }

  contextMenuClicked = ($event) => {
    this.onContextMenuEvent($event);
  }

  private setState() {
    if (this.editor) {
      // displayAsText over-rides readOnly
      if (this.displayAsText) {
        // no-op - is-preview class and data-options are on template

      } else if (this.isReadOnly) {
        this.editor.readonly = true;

      } else {
        this.editor.readonly = false;
        this.editor.disabled = false;
      }
    }
  }

  setValue(): void {
    if (this.editor && this.displayType !== LMRichTextTypeDisplayEnum.DISPLAY_AS_TEXT) {
      const currentElement = (this.editor as any).editor.getCurrentElement();

      // Need to keep the div and the textarea that are a part of the editor in sync
      if (currentElement[0].nodeName === 'TEXTAREA') {
        // in html/source mode
        currentElement.val(this.richTextValue);
        // visual/preview mode
        (this.editor as any).editor.element[0].innerHTML = this.richTextValue;
      } else {
        // in visual/preview mode
        currentElement.html(this.richTextValue);
        // html/source mode
        (this.editor as any).editor.textarea[0].innerText = this.richTextValue;
      }
    }
  }

  getValue(): string {
    if (this.editor && this.displayType !== LMRichTextTypeDisplayEnum.DISPLAY_AS_TEXT) {
      const currentElement = (this.editor as any).editor.getCurrentElement();
      let val;
      if (currentElement[0].nodeName === 'TEXTAREA') {
        // in html/source mode
        val = currentElement.val();
      } else {
        // in visual/preview mode
        val = currentElement.html();
      }

      return this.isNotBlank(val) ? val : '';
    }
  }

  // When entering text into the visual mode, soho will wrap it in paragraph tags if no other tags exist.
  // This paragraph tag isn't seen on the visual display, so when text is cleared the paragraph tags are
  // left. So, a field that appears blank on the screen could have a value of "<p><br/></p>". Check
  // if there is any value inside the paragraph tags that isn't a <br> or &nbsp to determine if
  // the value is blank or not
  isNotBlank(text: string): boolean {
    if(!!text) {
      let tempText = text.replace('<p>', '');
      tempText = tempText.replace('</p>', '');
      tempText = tempText.replace(/<br(\s+)?\/?>/g, '');
      tempText = tempText.replace('&nbsp;', '');
      return tempText.trim().length > 0;
    }
    return false;
  }
}
